// src/hooks/usePagePermissions.js

import { useState, useEffect } from 'react';
import useAuth from './useAuth';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

// Default permission state
const DEFAULT_PERMISSIONS = {
    create: false,
    edit: false,
    delete: false,
    view: false,
    export: false,
    import: false,
    approve: false,
    print: false,
    generate: false,
    track: false,
    return: false,
    pay: false,
    start: false,
    cancel: false,
    verify: false,
    produce: false,
    sync: false,
    manifest: false,
    move: false,
    duplicate: false,
    convert: false,
    pack: false,
    loading: true,
    hasAccess: false
};

/**
 * Custom Hook to evaluate active action-level capabilities.
 * @param {string} moduleKey - Unique ID of the view (e.g., 'usuarios')
 * @param {string} systemPreference - Optional system override ('base' or 'luxury')
 * @returns {Object} Permission state with boolean flags for each action
 */
const usePagePermissions = (moduleKey, systemPreference = null) => {
    const { currentUser } = useAuth();
    const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);

    useEffect(() => {
        const loadViewAccess = async () => {
            // Reset loading state
            setPermissions(prev => ({ ...prev, loading: true }));

            // If no user or role, return default permissions
            if (!currentUser?.role) {
                setPermissions({
                    ...DEFAULT_PERMISSIONS,
                    loading: false
                });
                return;
            }

            try {
                // Determine which system to check
                const preferredSystem = systemPreference ||
                    localStorage.getItem('erp_system_preference') ||
                    'base';

                // Build Firestore document ID
                const docId = `${currentUser.role}_${preferredSystem}`;
                const docRef = doc(db, 'module_permissions', docId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();

                    // Build permission object from Firestore data
                    const perms = {
                        // Check if module itself is accessible (for sidebar rendering)
                        hasAccess: data[moduleKey] === true,

                        // Granular action permissions
                        create: data[`${moduleKey}_create`] === true,
                        edit: data[`${moduleKey}_edit`] === true,
                        delete: data[`${moduleKey}_delete`] === true,
                        view: data[`${moduleKey}_view`] !== false ? true : false,
                        export: data[`${moduleKey}_export`] === true,
                        import: data[`${moduleKey}_import`] === true,
                        approve: data[`${moduleKey}_approve`] === true,
                        print: data[`${moduleKey}_print`] === true,
                        generate: data[`${moduleKey}_generate`] === true,
                        track: data[`${moduleKey}_track`] === true,
                        return: data[`${moduleKey}_return`] === true,
                        verify: data[`${moduleKey}_verify`] === true,
                        produce: data[`${moduleKey}_produce`] === true,
                        pay: data[`${moduleKey}_pay`] === true,
                        start: data[`${moduleKey}_start`] === true,
                        cancel: data[`${moduleKey}_cancel`] === true,
                        sync: data[`${moduleKey}_sync`] === true,
                        manifest: data[`${moduleKey}_manifest`] === true,
                        move: data[`${moduleKey}_move`] === true,
                        duplicate: data[`${moduleKey}_duplicate`] === true,
                        convert: data[`${moduleKey}_convert`] === true,
                        false: data[`${moduleKey}_convert`] === true,
                        loading: false
                    };

                    setPermissions(perms);
                } else {
                    // No permissions document found - deny all actions
                    setPermissions({
                        ...DEFAULT_PERMISSIONS,
                        loading: false
                    });
                }
            } catch (err) {
                console.error(`Failed to parse permissions for context: ${moduleKey}`, err);
                setPermissions({
                    ...DEFAULT_PERMISSIONS,
                    loading: false
                });
            }
        };

        loadViewAccess();
    }, [currentUser, moduleKey, systemPreference]);

    return permissions;
};

export default usePagePermissions;